import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CalendarIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CalendarPanel from './CalendarPanel';
import { formatUk, isValidIso, IsoDate } from './dateUtils';
import { useUniqueId } from './useUniqueId';

export type DateDropdownSize = 'sm' | 'md' | 'lg';
export type DateDropdownAlign = 'start' | 'end';

export interface DateDropdownProps {
  /** ISO YYYY-MM-DD on the wire. */
  value: IsoDate | null;
  onChange: (iso: IsoDate | null) => void;
  /** Visible placeholder text when value is empty. */
  placeholder?: string;
  /** Accessible label — required for screen readers; rendered visually only if no parent <label> wraps the trigger. */
  label?: string;
  /** Inclusive lower bound (ISO YYYY-MM-DD). */
  min?: IsoDate;
  /** Inclusive upper bound (ISO YYYY-MM-DD). */
  max?: IsoDate;
  disabled?: boolean;
  clearable?: boolean;
  size?: DateDropdownSize;
  /** Dropdown panel alignment relative to trigger. */
  align?: DateDropdownAlign;
  /** Pass-through id (set on the trigger button, used by parent <label htmlFor>). */
  id?: string;
  className?: string;
  'data-testid'?: string;
}

function classes(
  size: DateDropdownSize,
  open: boolean,
  disabled: boolean,
  empty: boolean,
  align: DateDropdownAlign,
  className?: string,
): string {
  return [
    'kk-date-dropdown',
    `kk-date-dropdown--${size}`,
    `kk-date-dropdown--align-${align}`,
    open ? 'kk-date-dropdown--open' : null,
    disabled ? 'kk-date-dropdown--disabled' : null,
    empty ? 'kk-date-dropdown--empty' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export default function DateDropdown({
  value,
  onChange,
  placeholder = 'Pick a date',
  label,
  min,
  max,
  disabled = false,
  clearable = true,
  size = 'md',
  align = 'start',
  id,
  className,
  'data-testid': dataTestId,
}: DateDropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const reactId = useUniqueId('date-dropdown');
  const popoverId = `${id ?? reactId}-popover`;

  const safeValue = isValidIso(value) ? value : null;
  const empty = safeValue === null;

  // Outside-click + Escape close. Re-focus the trigger on close so keyboard
  // users don't lose context.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // After opening, hand focus to the calendar grid so keyboard users can
  // navigate immediately.
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const grid = containerRef.current.querySelector<HTMLDivElement>(
      '.kk-date-panel__grid',
    );
    grid?.focus();
  }, [open]);

  function toggle() {
    if (disabled) return;
    setOpen((o) => !o);
  }

  function pick(iso: IsoDate) {
    onChange(iso);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function clear(e: React.MouseEvent) {
    // Stop the trigger from toggling open.
    e.stopPropagation();
    if (disabled) return;
    onChange(null);
  }

  return (
    <div
      ref={containerRef}
      className={classes(size, open, disabled, empty, align, className)}
      data-testid={dataTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="kk-date-dropdown__trigger"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={label}
      >
        <CalendarIcon
          className="kk-date-dropdown__lead-icon"
          width={16}
          height={16}
          aria-hidden="true"
        />
        <span className="kk-date-dropdown__value">
          {empty ? placeholder : formatUk(safeValue)}
        </span>
        {clearable && !empty && !disabled && (
          // Span with role=button — nesting a real <button> inside the
          // trigger button is invalid HTML and trips React + a11y warnings.
          <span
            role="button"
            tabIndex={0}
            className="kk-date-dropdown__clear"
            aria-label="Clear date"
            onClick={clear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
          >
            <XMarkIcon width={14} height={14} aria-hidden="true" />
          </span>
        )}
        <ChevronDownIcon
          className="kk-date-dropdown__chevron"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="kk-date-dropdown__popover"
          id={popoverId}
          role="dialog"
          aria-label={label ?? 'Pick a date'}
        >
          <CalendarPanel
            selectedIso={safeValue}
            minIso={min}
            maxIso={max}
            onPick={pick}
            ariaLabel={label ?? 'Pick a date'}
          />
        </div>
      )}
    </div>
  );
}
